import * as React from 'react';
import { useSelector } from 'react-redux';

import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import { Querystring } from 'utils/url';

import type { State } from 'types';

/** YouTube API Initialization code */
// @see https://developers.google.com/youtube/iframe_api_reference
// TODO: maybe someday we will need youtube controls, so it could be reverted to false/undefined
let isYoutubeScriptTagAdded = true;
let isYoutubeAPIReady;

function addYoutubeAPIScript() {
  if (isYoutubeScriptTagAdded) {
    return;
  }
  isYoutubeScriptTagAdded = true;

  const tag = document.createElement('script');
  tag.src = 'https://www.youtube.com/iframe_api';
  if (document.body) {
    document.body.appendChild(tag);
  }
}

window.onYouTubeIframeAPIReady = () => {
  isYoutubeAPIReady = true;
};
addYoutubeAPIScript();

/** End of YouTube API Initialization code */

function constructYoutubeIframe(id: string, autoplay?: boolean | null): string {
  const params = {
    autoplay: autoplay ? 1 : 0
  };

  return `https://www.youtube-nocookie.com/embed/${id}?${Querystring.objectToString(params)}`;
}

type Props = {
  name: string,
  videoId: string,
  width?: number,
  height?: number,
  isAutoplay?: boolean,
  onNextVideo?: () => void
};

const YouTubePlayer = (
  {
    name,
    videoId,
    width = 430,
    height = 242,
    isAutoplay = false,
    onNextVideo
  }: Props
) => {
  const userConsents = useSelector((state: State) => state.consentReducer.consents);

  React.useEffect(() => {
    if (!isYoutubeAPIReady) {
      return () => {};
    }

    // @ts-ignore - YT is coming from global 3rd party library
    const player = new YT.Player(`${name}-video-yt-content`, {
      videoId,
      width,
      height,
      host: 'https://www.youtube-nocookie.com',
      playerVars: { autoplay: (isAutoplay) ? 1 : 0 },
      events: {
        onStateChange: ({ data }) => {
          if (data === 0 && onNextVideo) { // on end
            onNextVideo();
          }
        }
      }
    });

    return () => {
      player.destroy();
    };
  }, [name, videoId, isAutoplay]);

  if (!userConsents.youtube) {
    return (
      <div style={{ width, height }}>
        <ConsentPlaceholder
          withLearnMore
          type="youtube"
          link={`https://www.youtube.com/watch?v=${videoId}`}
        />
      </div>
    );
  }

  return (
    <div id={`${name}-video-yt-content`}>
      {!isYoutubeAPIReady && ( // fallback to static iframe
        <iframe
          allowFullScreen
          id="static-ytplayer"
          width={width}
          height={height}
          src={constructYoutubeIframe(videoId, isAutoplay)}
          frameBorder="0"
        />
      )}
    </div>
  );
};

export default YouTubePlayer;
